import { FC, useEffect, useState } from 'react'
import {
  Text,
  GreyGrey90,
  IndigoIndigo60,
  Link,
  InputSelectControl,
  NumberFieldInput,
  Primary13Regular,
  RedRed60,
  SettingsFormControl,
  Switch,
  Box,
  Flex
} from '@sequencehq/core-components'
import {
  DEFAULT_INTERVAL_DAYS,
  DEFAULT_MAX_RETRIES,
  DEFAULT_RETRY_ANCHOR,
  DEFAULT_RETRY_OFFSETS,
  INTERVAL_OPTIONS,
  RETRY_ANCHOR_OPTIONS,
  RetryAnchor,
  buildScheduleOffsets,
  clampMaxRetries,
  intervalFromOffsets,
  maxRetriesError,
  maxRetriesFromOffsets,
  toWholeNumber
} from './stripeRetrySchedule.helpers'

const RETRY_DOCS_URL =
  'https://docs.sequencehq.com/connectors/stripe#automatically-retrying-failed-payments'

// A null anchor means automatic retries are off; a set anchor means they're on.
type StripeRetryScheduleValues = {
  retryAnchor: RetryAnchor | null
  retryOffsetDays: number[]
}

type StripeRetryScheduleSettingsProps = {
  settings: StripeRetryScheduleValues
  isPending: boolean
  onUpdate: (overrides: StripeRetryScheduleValues) => void
}

export const StripeRetryScheduleSettings: FC<
  StripeRetryScheduleSettingsProps
> = ({ settings, isPending, onUpdate }) => {
  const { retryAnchor, retryOffsetDays } = settings
  const interval = intervalFromOffsets(retryOffsetDays)
  const savedRetries = maxRetriesFromOffsets(retryOffsetDays)

  const [draftAnchor, setDraftAnchor] = useState<RetryAnchor | null>(
    retryAnchor
  )
  const [retriesDraft, setRetriesDraft] = useState(String(savedRetries))

  // Follow the saved values when they change from outside the form (initial load, refetch).
  useEffect(() => {
    setDraftAnchor(retryAnchor)
  }, [retryAnchor])
  useEffect(() => {
    if (retryAnchor !== null) setRetriesDraft(String(savedRetries))
  }, [retryAnchor, savedRetries])

  const save = (anchor: RetryAnchor | null, offsets: number[]) => {
    onUpdate({
      retryAnchor: anchor,
      retryOffsetDays: anchor === null ? [] : offsets
    })
  }

  const onToggleEnabled = (enabled: boolean) => {
    if (!enabled) {
      setDraftAnchor(null)
      save(null, [])
      return
    }
    setDraftAnchor(DEFAULT_RETRY_ANCHOR)
    setRetriesDraft(String(DEFAULT_MAX_RETRIES))
    save(DEFAULT_RETRY_ANCHOR, DEFAULT_RETRY_OFFSETS)
  }

  const onIntervalChange = (value: string) => {
    const days = toWholeNumber(value) ?? DEFAULT_INTERVAL_DAYS
    const retries = clampMaxRetries(toWholeNumber(retriesDraft) ?? savedRetries)
    save(draftAnchor, buildScheduleOffsets(days, retries))
  }

  const onAnchorChange = (value: string) => {
    const anchor = value as RetryAnchor
    setDraftAnchor(anchor)
    save(anchor, retryOffsetDays)
  }

  const onRetriesBlur = () => {
    if (draftAnchor === null) return
    const retries = clampMaxRetries(toWholeNumber(retriesDraft) ?? savedRetries)
    setRetriesDraft(String(retries))
    save(draftAnchor, buildScheduleOffsets(interval, retries))
  }

  const retriesFieldError = maxRetriesError(toWholeNumber(retriesDraft))

  return (
    <Box userSelect="none">
      <SettingsFormControl
        label="Automatically retry failed payments"
        htmlFor="automatic-retries-enabled"
      >
        <Switch
          size="md"
          id="automatic-retries-enabled"
          data-testid="settings.stripeRetry.enabledToggle"
          isChecked={draftAnchor !== null}
          disabled={isPending}
          onChange={onToggleEnabled}
        />
      </SettingsFormControl>

      {draftAnchor !== null && (
        <>
          <SettingsFormControl label="Retry">
            <Flex gap="8px" alignItems="center">
              <Box width="104px">
                <InputSelectControl
                  id="retry-interval"
                  data-testid="settings.stripeRetry.interval"
                  options={INTERVAL_OPTIONS}
                  value={String(interval)}
                  disabled={isPending}
                  onChange={onIntervalChange}
                  triggerStyle={{ width: '104px' }}
                />
              </Box>
              <Text {...Primary13Regular} color={GreyGrey90}>
                from
              </Text>
              <Box width="140px">
                <InputSelectControl
                  id="retry-anchor"
                  data-testid="settings.stripeRetry.anchor"
                  options={RETRY_ANCHOR_OPTIONS}
                  value={draftAnchor}
                  disabled={isPending}
                  onChange={onAnchorChange}
                  triggerStyle={{ width: '140px' }}
                />
              </Box>
            </Flex>
          </SettingsFormControl>

          <SettingsFormControl
            label="Number of successive retries"
            showBorderBottom={false}
            helper={
              retriesFieldError ? (
                <Box
                  as="span"
                  data-testid="settings.stripeRetry.retriesError"
                  color={RedRed60}
                >
                  {retriesFieldError}
                </Box>
              ) : (
                <>
                  Some retries are skipped or blocked by Stripe.{' '}
                  <Link
                    to={RETRY_DOCS_URL}
                    isExternal
                    color={IndigoIndigo60}
                    _hover={{ textDecoration: 'none' }}
                  >
                    Learn more →
                  </Link>
                </>
              )
            }
          >
            <NumberFieldInput
              data-testid="settings.stripeRetry.retries"
              aria-label="Number of successive retries"
              isInvalid={retriesFieldError !== null}
              min={1}
              value={retriesDraft}
              isDisabled={isPending}
              onChange={event => setRetriesDraft(event.target.value)}
              onBlur={onRetriesBlur}
              width="80px"
              height="32px"
            />
          </SettingsFormControl>
        </>
      )}
    </Box>
  )
}
